/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useEffect, useState, useRef, Dispatch, SetStateAction, FC } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isUndefined, isArray, isEqual, first, isObject, has, isNil } from 'lodash';
// Import Types
import type { FromToBlockItem, FromToBlocksRef } from 'components/common/FromToBlocks/types';
// Import UI Components
import { Modal, Button, BusyLoader } from '@geneui/components';
// Import Components
import { FromToBlocks } from 'components/common/FromToBlocks';
// Import Constants
import { l } from 'constants/common';
// Import Services
import { UtilsHttpService } from 'services/http';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Styles
import 'assets/scss/columnsChooser.scss';

interface Props {
    tableName: string;
    possibleColumns: Record<string, any>[];
    modalOpenedState: boolean;
    setModalOpenedState: Dispatch<SetStateAction<boolean>>;
    onSave: (columns: Record<string, any>[]) => void;
}

const ColumnsChooser: FC<Props> = ({ tableName, possibleColumns, modalOpenedState, setModalOpenedState, onSave }) => {
    const { t } = useTranslation();
    const { doPostRequest, doGetRequest } = useRequest();
    const possibleColumnsKeys = possibleColumns.map(({ dataKey }) => dataKey).join('|');

    // Segment fetch part
    const { saveDataTableConfig, getDataTableConfig } = useCancelableRequests(
        {
            saveDataTableConfig: UtilsHttpService.saveDataTableConfig,
            getDataTableConfig: UtilsHttpService.getDataTableConfig,
        },
        { takeLatest: ['getDataTableConfig'] },
    );

    const [leftList, setLeftList] = useState<FromToBlockItem[]>([]);
    const [rightList, setRightList] = useState<FromToBlockItem[]>([]);
    const [data, setData] = useState<Record<string, any>>({});
    const [selectedColumns, setSelectedColumns] = useState<Nilable<AnyObject[]>>();
    const [isSaveActive, setIsSaveActive] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTextLabel, setLoadingTextLabel] = useState(l.Loading);

    const fromToBlocksRef = useRef<FromToBlocksRef>(null);

    const findColumnByDataKey = (dataKey) => {
        return first(
            possibleColumns.filter((column) => {
                return column.dataKey === dataKey;
            }),
        );
    };

    const mapToColumnAPIModel = (list: AnyObject[]) => {
        if (!isArray(list)) return;

        return list.map(({ id, index }) => {
            return {
                Name: id,
                Position: index,
            };
        });
    };

    const mapPossibleColumnsToColumnAPIModel = (list: Record<string, any>[] = []) => {
        return list.map(({ dataKey }, index) => {
            return {
                Name: dataKey,
                Position: index,
            };
        });
    };

    const mapToBlockModelFromRemote = (list: Record<string, any>[] = [], possibleColumns: Record<string, any>[]) => {
        const possibleColumnDataKeys = possibleColumns.map((column) => column.dataKey);

        return list
            .filter(({ Name }) => {
                return !isUndefined(Name) && possibleColumnDataKeys.includes(Name);
            })
            .map(({ Name, Position }) => {
                const column = findColumnByDataKey(Name);
                return {
                    name: isObject(column) && has(column, 'text') ? column.text : Name,
                    id: Name,
                    isPermanent: isObject(column) && has(column, 'isPermanent') ? column.isPermanent : false,
                    isChecked: false,
                    isVisible: true,
                    index: Position,
                };
            });
    };

    const mapToBlockModelFromLocal = (enabledList: Record<string, any>[], list: Record<string, any>[] = []) => {
        return list
            .filter(({ text, dataKey }) => {
                return (
                    !isUndefined(text) &&
                    enabledList.filter((enabledColumn) => enabledColumn.id === dataKey).length === 0
                );
            })
            .map(({ text, dataKey, isPermanent }, index) => {
                return { name: text, id: dataKey, isPermanent, isChecked: false, isVisible: true, index: index };
            });
    };

    const mapSelectedToDataTable = (list: Nilable<Record<string, any>[]> = []) => {
        return (list ?? []).map((item) => {
            return item.Name;
        });
    };

    const getCurrentColumnsUpdate = (newData) => {
        setData(newData);
        setSelectedColumns(mapToColumnAPIModel(newData.rightBlockList));
    };

    useEffect(() => {
        if (!isArray(selectedColumns)) return;
        setIsSaveActive(!isEqual(mapToColumnAPIModel([...rightList]), selectedColumns));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedColumns]);

    const setBlocksValues = (left, right) => {
        setLeftList(left);
        setRightList(right);
    };

    const onSaveHandler = () => {
        setLoadingTextLabel(l.Saving);
        setIsLoading(true);
        setIsSaveActive(false);

        const requestBody = {
            TabelName: tableName,
            EnabledColumns: selectedColumns,
        };
        doPostRequest(saveDataTableConfig.request, {
            requestBody,
            successCallback: () => {
                setModalOpenedState(false);
                onSave(mapSelectedToDataTable(selectedColumns));
                setBlocksValues(data.leftBlockList, data.rightBlockList);
                setIsLoading(false);
            },
        });
    };

    const onResetHandler = () => {
        fromToBlocksRef.current?.reset?.();
    };

    const onCancelHandler = () => {
        setModalOpenedState(false);
    };

    const getCustomFooter = () => {
        return (
            <>
                <Button appearance="minimal" onClick={onCancelHandler}>
                    {t(l.Close)}
                </Button>
                <Button disabled={!isSaveActive} appearance="minimal" onClick={onResetHandler}>
                    {t(l.Reset)}
                </Button>

                <Button disabled={!isSaveActive} onClick={onSaveHandler}>
                    {t(l.Save)}
                </Button>
            </>
        );
    };

    const init = () => {
        doGetRequest(getDataTableConfig.request, {
            queryString: { tableName },
            successCallback: (Data) => {
                let { EnabledColumns } = Data;

                if (!isArray(EnabledColumns) || EnabledColumns.length === 0) {
                    EnabledColumns = mapPossibleColumnsToColumnAPIModel(
                        possibleColumns.filter((column) => !column.isHiddenByDefault),
                    );
                }

                const filteredEnabledColumns = mapToBlockModelFromRemote(EnabledColumns, possibleColumns);
                const filteredPossibleColumns = mapToBlockModelFromLocal(filteredEnabledColumns, possibleColumns);

                setBlocksValues(filteredPossibleColumns, filteredEnabledColumns);
                onSave(mapSelectedToDataTable(mapToColumnAPIModel(filteredEnabledColumns)));
            },
        });
    };

    // Re-sync chooser lists when the available columns change, e.g. after async custom fields load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [tableName, possibleColumnsKeys]);

    return (
        <div>
            {modalOpenedState && (
                <Modal
                    withPortal={true}
                    title={t(l.ColumnsChooser)}
                    className="d-a-d-popup"
                    background="dark-background"
                    closable={true}
                    position="center"
                    onCancel={onCancelHandler}
                    cancelText={t(l.Reset)}
                    okText={t(l.Save)}
                    closeOnClickOutside={true}
                    visible={modalOpenedState}
                    size="content-size"
                    footer={getCustomFooter()}
                >
                    {isLoading && (isNil(leftList) || isNil(rightList)) ? (
                        <BusyLoader isBusy type="spinner" loadingText={t(loadingTextLabel)} />
                    ) : (
                        <FromToBlocks
                            ref={fromToBlocksRef}
                            leftBlockList={leftList}
                            rightBlockList={rightList}
                            getUpdate={getCurrentColumnsUpdate}
                            leftBlockTitle={t(l.Possible)}
                            rightBlockTitle={t(l.Current)}
                            rightBlockWithPosition={true}
                        />
                    )}
                </Modal>
            )}
        </div>
    );
};

ColumnsChooser.propTypes = {
    tableName: PropTypes.string.isRequired,
    modalOpenedState: PropTypes.bool.isRequired,
    setModalOpenedState: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    possibleColumns: PropTypes.array.isRequired,
};

export default memo(ColumnsChooser);
